import { Router, configureRouter } from '@pkg/solid-page-router';
import { createEffect, lazy } from 'solid-js';
import { render } from 'solid-js/web';

import { QueryClientProvider } from '@mary/solid-query';

import { multiagent } from '~/api/globals/agent';
import { getCurrentAppView } from '~/api/globals/appview';
import { isOAuthCallback } from '~/api/globals/oauth';
import type { ModerationOptions } from '~/api/moderation';

import { ModalProvider } from '~/com/globals/modals';
import * as shared from '~/com/globals/shared';

import { useMediaQuery } from '~/utils/media-query';

import { MetaProvider } from '~/com/lib/meta';

import OAuthCallback from './components/OAuthCallback';
import ComposerContextProvider from './components/composer/ComposerContextProvider';
import MessagesContextProvider from './components/messages/MessagesContextProvider';
import { installChunkReloadGuard } from './lib/chunk-reload';
import { applyFont } from './globals/fonts';
import { queryClient } from './globals/query';
import { preferences } from './globals/settings';

import './styles/tailwind.css';

import('./lib/scheduled');

console.log('%c⊹ Skeetdeck', 'color:#3b82f6;font-size:20px;font-weight:700;letter-spacing:-0.5px;font-family:system-ui');
console.log('%cSharp. Deliberate. Fast.\n\nOpen source · https://github.com/j4ckxyz/skeetdeck', 'color:#6b7280;font-size:12px');

// Recover from stale deploys: if a lazy chunk (pane view, dialog, settings
// screen) fails to load because the cached app shell points at an asset hash
// that no longer exists on the server, reload once to fetch the fresh build
// instead of leaving the deck crashed.
installChunkReloadGuard();

const App = () => {
	createEffect(() => {
		// Sets up the multiagent labeler header
		multiagent.services.value = preferences.moderation.services.map((service) => ({
			did: service.did,
			redact: service.redact,
		}));
	});

	createEffect(() => {
		applyFont(preferences.ui.font);
	});

	// Switching the AppView changes the service that serves every timeline,
	// profile, search and notification. The networking layer already injects the
	// new service per-request, but cached query results from the previous AppView
	// would otherwise linger — so invalidate all queries to refetch from the
	// newly-selected AppView. Skips the initial run so we don't refetch on boot.
	createEffect((prevId: string | undefined) => {
		const id = getCurrentAppView().id;

		if (prevId !== undefined && prevId !== id) {
			queryClient.invalidateQueries();
		}

		return id;
	}, undefined);

	createEffect(() => {
		const theme = preferences.ui.theme;

		const cl = document.documentElement.classList;

		if (theme === 'auto') {
			const isDark = useMediaQuery('(prefers-color-scheme: dark)');

			createEffect(() => {
				cl.toggle('is-dark', isDark());
			});
		} else {
			cl.toggle('is-dark', theme === 'dark');
		}
	});

	return (
		<QueryClientProvider client={queryClient}>
			<MetaProvider>
				<ComposerContextProvider>
					<MessagesContextProvider>
						<Router />
						<ModalProvider />
					</MessagesContextProvider>
				</ComposerContextProvider>
			</MetaProvider>
		</QueryClientProvider>
	);
};

// Add polyfill for Promise.withResolvers, remove this later.
{
	Promise.withResolvers ??= function <T>(): PromiseWithResolvers<T> {
		let resolve!: (value: T | PromiseLike<T>) => void;
		let reject!: (reason?: any) => void;

		const promise = new this<T>((res, rej) => {
			resolve = res;
			reject = rej;
		});

		return { promise, resolve, reject };
	};
}

// Set up the router
configureRouter([
	{
		path: '/',
		component: lazy(() => import('./views/LandingView')),
	},
	{
		path: '/',
		component: lazy(() => import('./views/Layout')),
		children: [
			{
				path: '/decks/:deck',
				component: lazy(() => import('./views/DecksView')),
			},
			{
				path: '/settings',
				component: lazy(() => import('./views/SettingsView')),
			},
		],
	},
]);

// Set up common preferences
{
	const moderation = preferences.moderation;
	const createModerationOptions = (): ModerationOptions => {
		return {
			labels: moderation.labels,
			services: moderation.services,
			keywords: moderation.keywords,
			hideReposts: moderation.hideReposts,
			tempMutes: moderation.tempMutes,
		};
	};

	shared.setLanguagePreferences(preferences.language);
	shared.setModerationOptions(createModerationOptions());
	shared.setTranslationPreferences(preferences.translation);
	shared.setVerificationPreferences(preferences.verification);

	shared.setBustModerationListener(() => {
		shared.setModerationOptions({ ...shared.getModerationOptions(), ...createModerationOptions() });
	});
}

// The scroll restoration that Firefox does makes it broken, there's nothing to
// actually restore scroll position on anyway.
history.scrollRestoration = 'manual';

// When returning from an OAuth authorization redirect, complete the flow on a
// minimal screen instead of booting the full deck UI.
if (isOAuthCallback()) {
	render(() => <OAuthCallback />, document.body);
} else {
	render(() => <App />, document.body);
}
